import React from "react";
import "./landing.css";
import girlImage from "../../assets/new.png";
import boyImage from "../../assets/boyImg.svg";
import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";

function HeroSection() {
  const user = JSON.parse(localStorage.getItem("user"));
  const isLoggedIn = !!localStorage.getItem("access_token");
  const role = user?.role;
  return (
    <section className="bg-teal-50 px-4 sm:px-6 md:px-10 py-8">
      <h1 className="text-center text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2D1E5F] leading-tight md:leading-snug">
        Turning Learners into Champions!
      </h1>
      <div className="mb-6 md:mt-8 rounded-2xl px-4 py-6 md:px-8 md:py-10 border border-purple-600 shadow-xl md:shadow-2xl max-w-6xl mx-auto space-y-4 md:space-y-6 text-base md:text-lg text-gray-800 transition-all duration-300">

        {/* Heading */}
        <h2 className="text-2xl md:text-3xl font-extrabold text-center text-teal-700 relative">
          We help you help them!
          <span className="block w-24 md:w-32 h-1 bg-gradient-to-r from-teal-400 to-blue-400 mx-auto mt-2 rounded"></span>
        </h2>

        {/* Bullet Points */}
        <ul className="space-y-3 md:space-y-4 px-2 md:px-4">
          <li className="flex items-start gap-2 md:gap-3">
            <span className="text-teal-600 text-lg md:text-xl"><CheckCircle className="text-yellow-600 w-4 h-4 md:w-5 md:h-5 mt-0.5 md:mt-1" /></span>
            <span className="text-sm md:text-base">Are you a parent whose child is preparing for Opportunity Class or Selective Exam?</span>
          </li>
          <li className="flex items-start gap-2 md:gap-3">
            <span className="text-teal-600 text-lg md:text-xl"><CheckCircle className="text-yellow-600 w-4 h-4 md:w-5 md:h-5 mt-0.5 md:mt-1" /></span>
            <span className="text-sm md:text-base">Are you looking for some practice content for OC/ Selective tests?</span>
          </li>
          <li className="flex items-start gap-2 md:gap-3">
            <span className="text-teal-600 text-lg md:text-xl"><CheckCircle className="text-yellow-600 w-4 h-4 md:w-5 md:h-5 mt-0.5 md:mt-1" /></span>
            <span className="text-sm md:text-base">Are you worried about the exam preparation and want some mock tests (aligned to the actual exam)?</span>
          </li>
        </ul>

        {/* Highlight Box */}
        <div className="bg-white border-l-4 border-yellow-400 p-3 md:p-5 shadow-sm rounded-md">
          <p className="text-sm md:text-lg font-semibold text-yellow-700">
            ✅ If you have answered - yes to any of the questions above - this is the website you need to bookmark and sign up for your children.
          </p>
        </div>

        {/* Closing Note */}
        <p className="italic text-center text-gray-700 text-sm md:text-base">
          Our experienced team is passionate about learning. 🌟
        </p>
      </div>

      {/* Main Hero Content */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-10">

        {/* Girl Image - Hidden on small screens */}
        <div className="hidden md:flex justify-center md:justify-start w-full md:w-1/3">
          <img src={girlImage} alt="girl" className="w-[320px] md:w-[420px] object-contain" />
        </div>

        {/* Text Section */}
        <div className="text-center md:text-left w-full md:w-1/2 space-y-6 md:space-y-8">
          <div className="flex flex-row items-center justify-center gap-4 sm:gap-8 md:gap-32 relative pt-0 sm:pt-2">

            {/* Arrows - Hidden on small screens */}
            <svg
              className="hidden sm:block absolute top-6 left-[25%] w-60 h-16 z-0"
              viewBox="0 0 200 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 50 C70 0, 130 0, 200 50"
                stroke="#3B82F6"
                strokeWidth="3"
                fill="none"
                markerEnd="url(#arrowhead1)"
              />
              <defs>
                <marker
                  id="arrowhead1"
                  markerWidth="10"
                  markerHeight="10"
                  refX="5"
                  refY="5"
                  orient="auto"
                >
                  <path d="M0,0 L10,5 L0,10 Z" fill="#3B82F6" />
                </marker>
              </defs>
            </svg>

            <svg
              className="hidden sm:block absolute top-32 left-[25%] w-60 h-16 z-0"
              viewBox="0 0 200 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M200 50 C130 100, 70 100, 0 50"
                stroke="#10B981"
                strokeWidth="3"
                fill="none"
                markerEnd="url(#arrowhead2)"
              />
              <defs>
                <marker
                  id="arrowhead2"
                  markerWidth="10"
                  markerHeight="10"
                  refX="5"
                  refY="5"
                  orient="auto"
                >
                  <path d="M0,0 L10,5 L0,10 Z" fill="#10B981" />
                </marker>
              </defs>
            </svg>

            {/* Stat 1 */}
            <div className="bg-white shadow-lg sm:shadow-xl p-4 sm:p-6 rounded-full w-28 h-28 sm:w-44 sm:h-44 flex flex-col justify-center items-center border-4 border-blue-100 text-center relative sm:-mt-12 z-10 transition-all transform hover:scale-105 hover:shadow-xl sm:hover:shadow-2xl hover:bg-blue-50 hover:border-blue-500">
              <h2 className="text-xl sm:text-3xl font-bold text-teal-700">5k+</h2>
              <p className="text-xs sm:text-sm text-teal-600 mt-1 sm:mt-2">Questions to solve</p>
            </div>

            {/* Stat 2 */}
            <div className="bg-white shadow-lg sm:shadow-xl p-4 sm:p-6 rounded-full w-28 h-28 sm:w-44 sm:h-44 flex flex-col justify-center items-center border-4 border-blue-100 text-center relative z-10 transition-all transform hover:scale-105 hover:shadow-xl sm:hover:shadow-2xl hover:bg-green-50 hover:border-green-500">
              <h2 className="text-xl sm:text-3xl font-bold text-blue-800">30+</h2>
              <p className="text-xs sm:text-sm text-blue-600 mt-1 sm:mt-2">Tests per year level</p>
            </div>
          </div>
          <p className="bg-white border border-teal-500 text-teal-700 rounded-xl px-4 py-3 md:px-6 md:py-5 font-semibold shadow-md max-w-xl mx-auto md:mx-0 text-sm sm:text-base">
            Qualified tutors, proven strategies, and content. Start your child's OC/Selective journey with a free assessment.
          </p>
          <div className="mt-2 md:mt-4 flex flex-col sm:flex-row justify-center md:justify-between gap-3 sm:gap-4">
            <Link
              to="/exam-selection"
              className="rounded-full px-6 py-3 sm:px-8 sm:py-3 md:px-10 md:py-4 text-sm sm:text-md font-semibold bg-yellow-400 text-white shadow-md hover:bg-orange-500 transition duration-300 flex items-center justify-center gap-2"
            >
              Free Assessment

              <i className="fas fa-arrow-right"></i>
            </Link>
            {isLoggedIn ? (
              role === "admin" ? (
                <Link
                  to="/admin-dashboard"
                  className="rounded-full px-6 py-3 sm:px-8 sm:py-3 md:px-10 md:py-4 text-sm sm:text-md font-semibold bg-[#713EDD] text-white shadow-md hover:bg-teal-600 transition duration-300 flex items-center justify-center gap-2"
                >
                  Go to Admin Dashboard
                  <i className="fas fa-arrow-right"></i>
                </Link>
              ) : (
                <Link
                  to="/new-student-dashboard"
                  className="rounded-full px-6 py-3 sm:px-8 sm:py-3 md:px-10 md:py-4 text-sm sm:text-md font-semibold bg-[#713EDD] text-white shadow-md hover:bg-teal-600 transition duration-300 flex items-center justify-center gap-2"
                >
                  Go to Student Dashboard
                  <i className="fas fa-arrow-right"></i>
                </Link>
              )
            ) : (
              <Link
                to="/signup"
                className="rounded-full px-6 py-3 sm:px-8 sm:py-3 md:px-10 md:py-4 text-sm sm:text-md font-semibold bg-[#713EDD] text-white shadow-md hover:bg-teal-600 transition duration-300 flex items-center justify-center gap-2"
              >
                Sign Up
                <i className="fas fa-arrow-right"></i>
              </Link>
            )}
          </div>

        </div>

        {/* Boy Image - Hidden on small screens */}
        <div className="hidden md:flex justify-center md:justify-end w-full md:w-1/3 mt-[-20px] rounded-xl overflow-hidden">
          <img
            src={boyImage}
            alt="boy"
            className="w-[320px] md:w-[420px] object-cover"
          />
        </div>


      </div>

      {/* Horizontal Stats Section */}
      <div className=" flex flex-col items-center relative">

      </div>
    </section>
  );
}

export default HeroSection;